import { getSessionToken } from '@/api/sessionToken';
import { API_BASE } from '@/constants/api';
import axios, {
    type AxiosInstance,
    type AxiosRequestConfig,
    type AxiosResponse,
    type InternalAxiosRequestConfig,
  } from 'axios';
import { toast } from 'sonner';
import i18n from '@/i18n';
import { handleInactiveUser401 } from '@/api/authInactive';

export interface ApiClient {
  client: AxiosInstance;

  get<T>(path: string, params?: Record<string, any>): Promise<T>;
  post<T, U>(path: string, data: U, config?: AxiosRequestConfig): Promise<T>;
  put<T, U>(path: string, data: U, config?: AxiosRequestConfig): Promise<T>;
  delete<T>(path: string, config?: AxiosRequestConfig): Promise<T>;

  setHeader(name: string, value: string): void;
  removeHeader(name: string): void;

  addResponseInterceptor(
    onFulfilled: (response: AxiosResponse) => AxiosResponse | Promise<AxiosResponse>,
    onRejected?: (error: any) => any
  ): () => void;
  
  addRequestInterceptor(
    onFulfilled: (config: AxiosRequestConfig) => AxiosRequestConfig | Promise<AxiosRequestConfig>,
    onRejected?: (error: any) => any
  ): () => void;
}

/**
 * Removes null, undefined, and empty string values from an object.
 */
function cleanObject<T extends Record<string, any>>(obj?: T): T | undefined {
  if (!obj) return undefined;

  const cleaned: Record<string, any> = {};
  for (const key in obj) {
    const value = obj[key];
    if (value !== null && value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  }

  return Object.keys(cleaned).length > 0 ? (cleaned as T) : undefined;
}

/**
 * Axios-based implementation of ApiClient.
 */
export class AxiosClient implements ApiClient {
  public client: AxiosInstance;

  constructor(baseURL: string, defaultHeaders?: Record<string, string>) {
    // 获取超时时间配置，默认30秒
    const timeout = parseInt(import.meta.env.VITE_API_TIMEOUT || '300000', 10);
    
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        ...defaultHeaders,
      },
      timeout: timeout,
    });

    // Add request interceptor to automatically inject JWT token
    this.client.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        if (config.url?.includes('/system/auth/token/refresh')) return config;
        const token = await getSessionToken();
        if (token) config.headers.Authorization = `Bearer ${token}`;
        else delete config.headers.Authorization;
        return config;
      },
      (error: any) => Promise.reject(error)
    );

    // 用于防止重复toast的映射表
    const errorToastCache = new Map<string, number>();
    const ERROR_TOAST_THROTTLE = 3000; // 3秒内同一类型错误只显示一次toast
    
    // 检查是否应该显示toast
    const shouldShowToast = (key: string): boolean => {
      const now = Date.now();
      const lastShown = errorToastCache.get(key);
      if (!lastShown || now - lastShown > ERROR_TOAST_THROTTLE) {
        errorToastCache.set(key, now);
        return true;
      }
      return false;
    };

    // Add response interceptor to handle common errors
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      async (error: any) => {
        if (error.response) {
          const { status, data } = error.response;

          switch (status) {
            case 401:
              // 账号被停用是终态：Clerk session 仍健康、reload 永远成功，
              // 任何刷新/重试都只会再拿一个 401（僵尸循环）。直接统一登出。
              if (handleInactiveUser401(error)) {
                return Promise.reject(error);
              }

              // 检查是否是登录请求，如果是则不执行跳转（避免登录失败时页面刷新）
              const isLoginRequest = error.config?.url?.includes('/system/auth/login');
              const isVerifyUserRequest = error.config?.url?.includes('/system/user/verify-user');
              const isRefreshRequest = error.config?.url?.includes('/system/auth/token/refresh');

              // 如果是登录或验证用户请求，直接拒绝（不重试）
              if (isLoginRequest || isVerifyUserRequest || isRefreshRequest) {
                return Promise.reject(error);
              }

              if (error.config && !error.config._retry) {
                error.config._retry = true;
                try {
                  const token = await getSessionToken(true);
                  if (token) return this.client.request(error.config);
                } catch {
                  // The terminal 401 path below ends the shared session.
                }
              }

              // 如果没有 Clerk session，清除旧 token 并发送登出事件
              localStorage.removeItem('ACCESS_TOKEN');
              localStorage.removeItem('REFRESH_TOKEN');

              console.log('🚨 ApiClient: 401错误，发送登出事件');
              // 发送自定义事件通知应用进行登出
              window.dispatchEvent(new CustomEvent('auth:unauthorized', {
                detail: { error: data, status: 401 }
              }));
              break;
            case 403:
              // 403错误只在一定时间内显示一次toast，避免重复弹出
              if (shouldShowToast('403')) {
                toast.error(i18n.t('common:toast.noPermission'));
              }
              // 静默处理403错误，不重复toast
              console.warn('🚫 403 Permission Denied:', error.config?.url);
              break;
            case 404:
              if (shouldShowToast('404')) {
                toast.error(i18n.t('common:toast.notFound'));
              }
              break;
            case 500:
              if (shouldShowToast('500')) {
                toast.error(i18n.t('common:toast.internalServerError'));
              }
              break;
            default:
              const errorKey = `${status}-${data?.message}`;
              if (shouldShowToast(errorKey)) {
                toast.error(data?.message || i18n.t('common:toast.requestFailed'));
              }
          }
        } else if (error.request) {
          // Network error
          if (shouldShowToast('network')) {
            toast.error(i18n.t('common:toast.networkError'));
          }
        } else {
          // Other errors
          if (shouldShowToast('config')) {
            toast.error(i18n.t('common:toast.requestConfigError'));
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  setHeader(name: string, value: string) {
    this.client.defaults.headers.common[name] = value;
  }

  removeHeader(name: string) {
    delete this.client.defaults.headers.common[name];
  }

  addResponseInterceptor(onFulfilled: any, onRejected?: any) {
    const id = this.client.interceptors.response.use(onFulfilled, onRejected);
    return () => this.client.interceptors.response.eject(id);
  }

  addRequestInterceptor(onFulfilled: any, onRejected?: any) {
    const id = this.client.interceptors.request.use(onFulfilled, onRejected);
    return () => this.client.interceptors.request.eject(id);
  }

  async get<T>(path: string, params?: Record<string, any>): Promise<T> {
    const cleanedParams = cleanObject(params);
    const response: AxiosResponse<T> = await this.client.get(path, { params: cleanedParams });
    return response.data;
  }

  async post<T, U>(path: string, data: U, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(path, data, config);
    return response.data;
  }

  async put<T, U>(path: string, data: U, config?: AxiosRequestConfig): Promise<T> {
    const cleanedData = cleanObject(data as Record<string, any>);
    const response: AxiosResponse<T> = await this.client.put(path, cleanedData, config);
    return response.data;
  }

  async delete<T>(path: string, config?: AxiosRequestConfig): Promise<T> {
    const cleanedParams = cleanObject(config?.params);
    const response: AxiosResponse<T> = await this.client.delete(path, {
      ...config,
      params: cleanedParams,
    });
    return response.data;
  }
}

// ImportMeta 类型由 src/vite-env.d.ts 的 vite/client 提供，
// 这里原有的 declare global 重复声明会与之冲突（TS2687/2717），已移除。

export const apiClient: ApiClient = new AxiosClient(
  (import.meta.env && import.meta.env.VITE_API_BASE) || API_BASE
);

export default apiClient;